import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useActivityTypes, useCreateSession, useTrainerSessions } from "../../lib/queries";
import { Loading, ErrorBox } from "../../components/Loading";
import { closeApp, hapticNotification, setBackButton } from "../../lib/tg";
import { useToast } from "../../components/Toast";

const DURATION_OPTIONS = [60, 90, 120, 180]; // minutes

export default function NewSession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const activitiesQ = useActivityTypes();
  const sessionsQ = useTrainerSessions(user?.trainer_id ?? undefined);
  const create = useCreateSession();

  const [activityId, setActivityId] = useState<string | null>(null);
  const [date, setDate] = useState<string>(today());
  const [startTime, setStartTime] = useState("10:00");
  const [durationMin, setDurationMin] = useState(90);
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState(10);
  const [notes, setNotes] = useState("");

  useEffect(() => setBackButton(() => navigate("/t")), [navigate]);

  const activity = useMemo(
    () => (activitiesQ.data ?? []).find((a) => a.id === activityId),
    [activitiesQ.data, activityId],
  );

  // Pre-fill capacity and duration from selected activity
  useEffect(() => {
    if (activity) {
      setCapacity(activity.default_capacity);
      setDurationMin(activity.default_duration_min);
    }
  }, [activity]);

  // Computed end time from start + duration
  const endTime = useMemo(() => {
    const [h, m] = startTime.split(":").map(Number);
    const endTotal = h * 60 + m + durationMin;
    return `${pad(Math.floor((endTotal / 60) % 24))}:${pad(endTotal % 60)}`;
  }, [startTime, durationMin]);

  // Location suggestions: standard ones from activity + recent history.
  const locationSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const standards = activity?.settings?.default_locations ?? [];
    for (const loc of standards) {
      if (!seen.has(loc)) { seen.add(loc); out.push(loc); }
    }
    for (const s of sessionsQ.data ?? []) {
      if (s.activity_type_id !== activityId) continue;
      if (s.location_text && !seen.has(s.location_text)) {
        seen.add(s.location_text); out.push(s.location_text);
        if (out.length >= 8) break;
      }
    }
    return out;
  }, [activity, activityId, sessionsQ.data]);

  if (activitiesQ.isLoading) return <Loading />;
  if (activitiesQ.error) return <ErrorBox message={(activitiesQ.error as Error).message} />;

  const canSubmit = activityId && date && startTime && durationMin > 0 && capacity > 0 && user?.trainer_id;

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">Нове заняття</h1>

      <section className="card">
        <h3 className="font-semibold mb-2">Тип</h3>
        <div className="flex flex-wrap gap-2">
          {(activitiesQ.data ?? []).map((a) => (
            <button
              key={a.id}
              className={`chip text-base ${activityId === a.id ? "bg-tg-button text-tg-buttonText" : ""}`}
              onClick={() => setActivityId(a.id)}
            >{a.emoji} {a.name}</button>
          ))}
        </div>
      </section>

      <section className="card space-y-3">
        <Field label="Дата">
          <div className="flex gap-2 items-center flex-wrap">
            <button
              className={`chip ${date === today() ? "bg-tg-button text-tg-buttonText" : ""}`}
              onClick={() => setDate(today())}
            >Сьогодні</button>
            <button
              className={`chip ${date === tomorrow() ? "bg-tg-button text-tg-buttonText" : ""}`}
              onClick={() => setDate(tomorrow())}
            >Завтра</button>
            <input type="date" className="flex-1 bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2 min-w-[140px]"
              value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </Field>

        <Field label="Початок">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="time" className="bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
              value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            {(activity?.settings?.default_start_times ?? []).map((t) => (
              <button
                key={t}
                className={`chip ${startTime === t ? "bg-tg-button text-tg-buttonText" : ""}`}
                onClick={() => setStartTime(t)}
              >{t}</button>
            ))}
          </div>
        </Field>

        <Field label={`Тривалість · до ${endTime}`}>
          <div className="flex flex-wrap gap-2">
            {DURATION_OPTIONS.map((mins) => (
              <button
                key={mins}
                className={`chip ${durationMin === mins ? "bg-tg-button text-tg-buttonText" : ""}`}
                onClick={() => setDurationMin(mins)}
              >{fmtDuration(mins)}</button>
            ))}
            <input
              type="number"
              min={15}
              max={480}
              step={15}
              className="w-20 bg-tg-bg border border-tg-secondary rounded-xl px-2 py-1 text-sm"
              value={durationMin}
              onChange={(e) => setDurationMin(Number(e.target.value) || 0)}
              title="хв"
            />
            <span className="text-tg-hint self-center text-sm">хв</span>
          </div>
        </Field>

        <Field label="Локація">
          {locationSuggestions.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {locationSuggestions.map((loc) => (
                <button
                  key={loc}
                  className={`chip text-sm ${location === loc ? "bg-tg-button text-tg-buttonText" : ""}`}
                  onClick={() => setLocation(loc)}
                >{loc}</button>
              ))}
            </div>
          )}
          <input className="w-full bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
            placeholder="або власна локація"
            value={location} onChange={(e) => setLocation(e.target.value)} />
        </Field>

        <Field label="Кількість місць">
          <input type="number" min={1} className="w-24 bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
            value={capacity} onChange={(e) => setCapacity(Number(e.target.value) || 0)} />
        </Field>

        <Field label="Нотатки (необов'язково)">
          <textarea className="w-full bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
            rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </section>

      <button
        className="btn-primary w-full"
        disabled={!canSubmit || create.isPending}
        onClick={async () => {
          try {
            const startsAt = new Date(`${date}T${startTime}`).toISOString();
            const endsAt = new Date(new Date(startsAt).getTime() + durationMin * 60_000).toISOString();
            await create.mutateAsync({
              trainer_id: user!.trainer_id!,
              activity_type_id: activityId!,
              starts_at: startsAt,
              ends_at: endsAt,
              location_text: location.trim() || null,
              capacity,
              notes: notes.trim() || null,
            });
            hapticNotification("success");
            toast.success("Опубліковано");
            closeApp();
          } catch (e) {
            hapticNotification("error");
            toast.error((e as Error).message);
          }
        }}
      >Створити і опублікувати</button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-sm text-tg-hint mb-1">{label}</div>
      {children}
    </div>
  );
}

function fmtDuration(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}хв`;
  if (m === 0) return `${h}г`;
  return `${h}г${m}`;
}

const pad = (n: number) => n.toString().padStart(2, "0");
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function tomorrow(): string {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
